/**
 * Environment variable validation and management
 * Provides safe access to environment variables with validation
 */

// Required environment variables for the application
const REQUIRED_ENV_VARS = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID',
];

// Optional environment variables with fallbacks
const OPTIONAL_ENV_VARS = {
  REACT_APP_DEBUG: 'false',
  REACT_APP_ENVIRONMENT: 'development',
};

/**
 * Get environment variable with validation
 * @param {string} key - Environment variable key
 * @param {string} fallback - Fallback value for optional variables
 * @param {boolean} required - Whether the variable is required
 * @returns {string} Environment variable value
 */
const getEnv = (key, fallback = undefined, required = false) => {
  const value = process.env[key];

  if (value === undefined) {
    if (required) {
      throw new Error(`Required environment variable ${key} is not defined`);
    }
    return fallback;
  }

  return value;
};

/**
 * Validate all required environment variables
 * Throws an error if any required variables are missing
 */
const validateEnvironment = () => {
  const missingVars = [];

  REQUIRED_ENV_VARS.forEach((varName) => {
    if (!process.env[varName]) {
      missingVars.push(varName);
    }
  });

  if (missingVars.length > 0) {
    const errorMessage = `
❌ Missing required environment variables:

${missingVars.map((varName) => `  - ${varName}`).join('\n')}

Please check your .env file and ensure all required variables are set.

Required variables:
${REQUIRED_ENV_VARS.map((varName) => `  - ${varName}`).join('\n')}

Example .env file:
${REQUIRED_ENV_VARS.map((varName) => `${varName}=your_value_here`).join('\n')}
    `.trim();

    throw new Error(errorMessage);
  }
};

// Validate environment on module load
try {
  // validateEnvironment();
} catch (error) {
  // Display error in console for development
  console.error('🚨 Environment Configuration Error:', error.message);

  // In development, show a more user-friendly error
  if (process.env.NODE_ENV === 'development') {
    // Create a visible error display in the browser
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #ff4444;
      color: white;
      padding: 20px;
      font-family: monospace;
      font-size: 14px;
      z-index: 9999;
      white-space: pre-line;
      box-shadow: 0 2px 10px rgba(0,0,0,0.3);
    `;
    errorDiv.textContent = error.message;
    document.body.appendChild(errorDiv);
  }

  // Re-throw to prevent app from starting
  throw error;
}

// Export validated environment variables
export const ENV = {
  // Required Firebase configuration
  FIREBASE_API_KEY: getEnv('REACT_APP_FIREBASE_API_KEY', undefined, false),
  FIREBASE_AUTH_DOMAIN: getEnv('REACT_APP_FIREBASE_AUTH_DOMAIN', undefined, false),
  FIREBASE_PROJECT_ID: getEnv('REACT_APP_FIREBASE_PROJECT_ID', undefined, false),
  FIREBASE_STORAGE_BUCKET: getEnv('REACT_APP_FIREBASE_STORAGE_BUCKET', undefined, false),
  FIREBASE_MESSAGING_SENDER_ID: getEnv('REACT_APP_FIREBASE_MESSAGING_SENDER_ID', undefined, false),
  FIREBASE_APP_ID: getEnv('REACT_APP_FIREBASE_APP_ID', undefined, false),

  // Optional configuration
  DEBUG: getEnv('REACT_APP_DEBUG', OPTIONAL_ENV_VARS['REACT_APP_DEBUG']),
  ENVIRONMENT: getEnv('REACT_APP_ENVIRONMENT', OPTIONAL_ENV_VARS['REACT_APP_ENVIRONMENT']),
};

// Export validation function for testing
export { validateEnvironment };

export default ENV;
